import { useQuery } from '@tanstack/react-query';
import { fetchHealth, fetchMeta } from '../api';

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline gap-4 border-b border-[var(--color-border-subtle)] py-2 last:border-0">
      <span className="w-56 shrink-0 text-[var(--color-ink-muted)]">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

/**
 * Única pantalla con datos reales en H1: estado del propio portal.
 * El dashboard y la vista de proyecto llegan en H3 y H4.
 */
export function StatusPage(): JSX.Element {
  const health = useQuery({ queryKey: ['health'], queryFn: fetchHealth, refetchInterval: 30_000 });
  const meta = useQuery({ queryKey: ['meta'], queryFn: fetchMeta });

  return (
    <section className="max-w-3xl">
      <h1 className="mb-1 text-lg font-semibold">Estado del sistema</h1>
      <p className="mb-6 text-[var(--color-ink-muted)]">
        Andamiaje (H1) desplegado. La ingesta de datos de GitHub es H2.
      </p>

      <div className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-4 py-3">
        {health.isPending && <p className="text-[var(--color-ink-muted)]">Comprobando…</p>}
        {health.isError && <p className="text-red-600">La API no responde.</p>}
        {health.data && (
          <>
            <Row
              label="Estado general"
              value={health.data.status === 'ok' ? 'Operativo' : 'Degradado'}
            />
            <Row label="Versión" value={health.data.version} />
            <Row label="Entorno" value={meta.data?.environment ?? 'no disponible'} />
            <Row label="Tiempo en marcha" value={`${health.data.uptimeSeconds} s`} />
            <Row
              label="Base de datos"
              value={
                health.data.checks.database.status === 'up'
                  ? `Arriba (${health.data.checks.database.latencyMs} ms)`
                  : (health.data.checks.database.detail ?? 'Abajo')
              }
            />
            <Row
              label="Credencial de GitHub"
              value={
                meta.data
                  ? meta.data.github.configured
                    ? `Configurada (modo ${meta.data.github.mode})`
                    : 'No configurada'
                  : 'no disponible'
              }
            />
          </>
        )}
      </div>

      {meta.data && !meta.data.github.configured && (
        <p className="mt-4 text-[13px] text-[var(--color-ink-muted)]">
          Siguiente paso: emitir el token de solo lectura descrito en{' '}
          <code>docs/08-permisos-github.md</code> y reiniciar <code>scc-api</code> y{' '}
          <code>scc-worker</code>.
        </p>
      )}
    </section>
  );
}
